// ─── THESIS BUILDER ───────────────────────────────────────────────────────────
// Five steps and a review screen. The user authors a thesis; the platform
// watches it. Nothing here recommends anything, and the copy is written so it
// cannot be mistaken for advice.
//
// ⚠⚠ CONTRARIAN IS ORANGE, NOT AMBER. C.amber and C.teal are the SAME VALUE in
// this palette — both "#00FF87" — so an amber contrarian button would have
// rendered a green almost identical to BULLISH, which is the one pair that must
// never be confusable. C.orange (#f97316) is the only genuinely distinct third
// colour available.

import { useState, useEffect, useRef, useCallback } from "react";
import { C, s, font, backendFetch } from "../core";

const SECTOR_ICONS = {
  Technology: "💻",
  Semiconductors: "⚡",
  Energy: "🛢",
  Financials: "🏦",
  Healthcare: "🧬",
  Industrials: "⚙️",
  Consumer: "🛍",
  Macro: "🌐",
  "Crypto-adjacent": "₿",
  Defense: "🛡",
};

const DIRECTIONS = [
  { id: "bullish", label: "BULLISH", color: C.green },
  { id: "bearish", label: "BEARISH", color: C.red },
  { id: "contrarian", label: "CONTRARIAN", color: C.orange },
];
const STYLES = ["growth", "value", "income", "momentum"];
const TIMEFRAMES = [
  { id: "days", label: "DAYS", desc: "Intraday to 72-hour signals" },
  { id: "weeks", label: "WEEKS", desc: "Multi-day to weekly signals" },
  { id: "months", label: "MONTHS", desc: "Weekly to monthly signals" },
  { id: "quarters", label: "QUARTERS", desc: "Monthly to quarterly signals" },
];

// ⚠ Plain English, one sentence each. A priority the user cannot interpret is a
// priority they will set at random.
const SIGNAL_TYPES = [
  ["dark_pool", "Dark Pool Activity",
   "Large institutional block trades executed off-exchange, indicating where smart money is positioning."],
  ["unusual_options", "Unusual Options Flow",
   "Option trades far larger than a contract's normal volume, often placed ahead of a move."],
  ["congressional", "Congressional Trades",
   "Stock transactions disclosed by members of Congress and their households."],
  ["insider", "Insider Filings (Form 4)",
   "Buying and selling by a company's own officers, directors and large holders."],
  ["legislation", "Legislative Activity",
   "Bills and committee action that would change the rules a sector operates under."],
  ["autocall_barrier", "Autocallable Barriers",
   "Price levels in structured notes where dealer hedging can force buying or selling."],
  ["physical", "Physical World (Flights & Vessels)",
   "Corporate aircraft and tanker movements that show activity before it is announced."],
  ["macro_sentiment", "Macro Sentiment",
   "Cross-asset positioning and risk appetite across rates, credit and volatility."],
];

const NOTE_MAX = 2000;
const NOTE_WARN = 1800;
// ⚠⚠ MATCHES thesis_service.NOTE_MIN. The service rejects a shorter note with a
// 400, so a permissive form would let a user write six steps of thesis and lose
// it at the final button. Same two-halves-must-agree rule as
// MAX_TICKERS_PER_SECTOR above.
const NOTE_MIN = 150;
// ⚠ Rendered ABOVE the textarea, permanently — not as placeholder text, which
// disappears on the first keystroke and takes the prompt with it.
const NOTE_QUESTIONS = [
  "What is your thesis, in your own words?",
  "What do you see that others don't?",
  "What would prove you right?",
  "What would prove you wrong?",
];
// ⚠ SIX numbered steps now. The review screen is still a boolean, not step 7 —
// it is a confirmation of the six, not another thing to fill in.
const STEPS = 6;

const MAX_SECTORS = 3;
// ⚠⚠ NOW A BLOCK, NOT A NUDGE, AND THE SERVICE AGREES. This was named
// SOFT_TICKER_LIMIT: the counter read "5 of 5 max", the hint said focused theses
// perform better, and then it let a sixth name in anyway.
// thesis_service._validate now rejects any sector group over this, so leaving the
// UI permissive would let a user build a basket the save silently 422s.
// ⚠ Hardcoded to match thesis_service.MAX_TICKERS_PER_SECTOR. The API serves the
// same number as `max_tickers_per_sector` on /api/thesis/sectors; this stays a
// constant so a cold or failed taxonomy fetch cannot leave the builder with no
// limit at all, which is the failure mode that lets a user fill a form the
// backend will refuse.
const MAX_TICKERS_PER_SECTOR = 5;
const NAME_MAX = 50;
const NAME_OK = /^[A-Za-z0-9][A-Za-z0-9 \-]*$/;

const wrap = { fontFamily: font, color: C.text, fontSize: 13, lineHeight: 1.6 };

function StepDots({ step }) {
  return (
    <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
      {Array.from({ length: STEPS }, (_, i) => i + 1).map((n) => (
        <div key={n} style={{
          height: 3, flex: 1, borderRadius: 2,
          background: n <= step ? C.green : C.border,
        }} />
      ))}
    </div>
  );
}

function Choice({ active, color, children, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        minHeight: 44, padding: "8px 14px", borderRadius: 3, cursor: disabled ? "default" : "pointer",
        fontFamily: font, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
        background: active ? `${color || C.green}22` : "transparent",
        color: active ? (color || C.green) : C.textDim,
        border: `1px solid ${active ? (color || C.green) : C.border}`,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

export function ThesisBuilder({ onBuilt }) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState("");
  const [style, setStyle] = useState("");
  const [timeframe, setTimeframe] = useState("");
  const [thesisName, setThesisName] = useState("");
  const [taxonomy, setTaxonomy] = useState({});
  const [selectedSectors, setSelectedSectors] = useState([]);
  // sector -> [{ticker, name, sector, custom?}]
  const [selectedTickers, setSelectedTickers] = useState({});
  const [signalWeights, setSignalWeights] = useState(() =>
    Object.fromEntries(SIGNAL_TYPES.map(([id]) => [id, "medium"])));
  const [openInfo, setOpenInfo] = useState(null);
  const [note, setNote] = useState("");
  const [review, setReview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // per-sector search state
  const [search, setSearch] = useState({});      // sector -> query
  const [results, setResults] = useState({});    // sector -> [rows]
  const [searching, setSearching] = useState({});
  const timers = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await backendFetch("/api/thesis/sectors", "/api/thesis/sectors");
        if (!r.ok || cancelled) return;
        const d = await r.json();
        if (!cancelled) setTaxonomy(d?.sectors || {});
      } catch { /* the builder still works; sector cards just stay empty */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => () => {
    Object.values(timers.current).forEach(clearTimeout);
  }, []);

  const runSearch = useCallback((sector, q) => {
    clearTimeout(timers.current[sector]);
    if (!q.trim()) {
      setResults((p) => ({ ...p, [sector]: [] }));
      return;
    }
    // ⚠ 300ms debounce. Each keystroke would otherwise be an upstream asset
    // search, and the Yahoo fallback behind it is a third party.
    timers.current[sector] = setTimeout(async () => {
      setSearching((p) => ({ ...p, [sector]: true }));
      try {
        const path = `/api/thesis/tickers/search?q=${encodeURIComponent(q.trim())}`;
        const r = await backendFetch(path, path);
        const d = r.ok ? await r.json() : { results: [] };
        setResults((p) => ({ ...p, [sector]: d?.results || [] }));
      } catch {
        setResults((p) => ({ ...p, [sector]: [] }));
      } finally {
        setSearching((p) => ({ ...p, [sector]: false }));
      }
    }, 300);
  }, []);

  const toggleSector = (name) => {
    setSelectedSectors((prev) => {
      if (prev.includes(name)) {
        // ⚠ Deselecting also drops that sector's tickers — leaving them would
        // save tickers under a sector the thesis no longer claims.
        setSelectedTickers((t) => { const n = { ...t }; delete n[name]; return n; });
        return prev.filter((x) => x !== name);
      }
      if (prev.length >= MAX_SECTORS) return prev;   // locked; see the dim state
      return [...prev, name];
    });
  };

  const toggleTicker = (sector, row) => {
    setSelectedTickers((prev) => {
      const cur = prev[sector] || [];
      const has = cur.some((t) => t.ticker === row.ticker);
      // ⚠ Removal is ALWAYS allowed, even at the cap — the guard below must not
      // trap a user who has hit the limit with no way back under it.
      if (has) return { ...prev, [sector]: cur.filter((t) => t.ticker !== row.ticker) };
      // ⚠⚠ THE HALF OF THE RULE THAT SURVIVES A CRAFTED REQUEST IS IN
      // thesis_service._validate; this is the half that keeps the form honest.
      // Same shape as toggleSector's MAX_SECTORS guard directly above.
      if (cur.length >= MAX_TICKERS_PER_SECTOR) return prev;
      return { ...prev, [sector]: [...cur, row] };
    });
  };

  const allTickers = Object.values(selectedTickers).flat();
  const canStep1 = direction && style && timeframe;
  const canStep2 = thesisName.trim().length > 0
    && thesisName.trim().length <= NAME_MAX
    && NAME_OK.test(thesisName.trim());
  const canStep3 = selectedSectors.length > 0;
  const canStep4 = allTickers.length > 0;
  // ⚠ The note is REQUIRED now, so step 6 has a gate like every other step.
  const canStep6 = note.trim().length >= NOTE_MIN;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        thesis_name: thesisName.trim(),
        direction, style, timeframe,
        sectors: selectedSectors,
        tickers: allTickers.map(({ ticker, name, sector }) => ({ ticker, name, sector })),
        signal_weights: signalWeights,
        // ⚠⚠ THE NOTE TRAVELS WITH THE CREATE NOW. It used to be a second,
        // sequential PATCH fired after the thesis existed, whose failure was
        // swallowed by an empty catch — and that PATCH was CORS-blocked for the
        // life of the feature, so every note written here was silently discarded
        // and the thesis was built without one. Creation is atomic: no note, no
        // thesis, and any rejection surfaces in `error` below.
        note: note.trim().slice(0, NOTE_MAX),
      };
      const r = await backendFetch("/api/thesis", "/api/thesis", {
        method: "POST", body: JSON.stringify(body),
      });
      if (!r.ok) {
        let msg = "Could not build the thesis. Try again.";
        try { const e = await r.json(); if (e?.detail) msg = String(e.detail); } catch { /* keep */ }
        setError(msg);
        return;
      }
      const created = await r.json();

      // ⚠⚠ ONE CALL. This used to be followed by a best-effort PATCH that wrote
      // the note after the thesis existed, with its failure swallowed by an empty
      // catch. POST /api/thesis carries the note now and rejects a thesis without
      // one, so there is nothing left to write and nothing left to lose: the
      // response already has thesis_note populated.
      onBuilt?.(created);
    } catch {
      setError("Could not reach the platform. Check your connection and try again.");
    } finally {
      setSaving(false);
    }
  };

  // ── REVIEW ────────────────────────────────────────────────────────────────
  if (review) {
    const high = SIGNAL_TYPES.filter(([id]) => signalWeights[id] === "high").map(([, l]) => l);
    return (
      <div style={{ ...wrap, maxWidth: 720 }}>
        <div style={{ fontSize: 11, color: C.textDim, letterSpacing: "0.12em", marginBottom: 10 }}>
          REVIEW
        </div>
        <div style={{ ...s.panel, padding: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.green }}>{thesisName.trim()}</div>
          <div style={{ color: C.textDim, marginBottom: 12 }}>
            {direction.toUpperCase()} · {style.toUpperCase()} · {timeframe.toUpperCase()}
          </div>
          {selectedSectors.map((sec) => (
            <div key={sec} style={{ marginBottom: 6 }}>
              <span style={{ color: C.textMid }}>{SECTOR_ICONS[sec] || ""} {sec}: </span>
              <span style={{ color: C.text }}>
                {(selectedTickers[sec] || []).map((t) => t.ticker).join(", ") || "—"}
              </span>
            </div>
          ))}
          <div style={{ marginTop: 12, color: C.textMid }}>High priority signals:</div>
          <div style={{ color: C.text }}>{high.length ? high.join(", ") : "None — all medium or low"}</div>
          {note.trim() && (
            <>
              <div style={{ marginTop: 12, color: C.textDim, fontSize: 11,
                            letterSpacing: "0.1em" }}>
                THESIS NOTE
              </div>
              {/* ⚠ Clamped to three lines rather than truncated with slice():
                  cutting mid-word in a review the user is about to confirm reads
                  as data loss. -webkit-line-clamp degrades to a plain block
                  where unsupported, which shows more text, never less. */}
              <div style={{
                color: C.text, fontStyle: "italic", lineHeight: 1.6,
                display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}>
                {note.trim()}
              </div>
            </>
          )}
        </div>
        {error && (
          <div style={{ marginTop: 10, color: C.red, fontSize: 12 }}>{error}</div>
        )}
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button style={{ ...s.btn(), minHeight: 44 }} onClick={() => setReview(false)}>
            ← BACK TO EDIT
          </button>
          <button style={{ ...s.btn("green"), minHeight: 44 }} onClick={submit} disabled={saving}>
            {saving ? "BUILDING…" : "BUILD THESIS →"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...wrap, maxWidth: 720 }}>
      <StepDots step={step} />

      {/* ── STEP 1 — orientation ── */}
      {step === 1 && (
        <>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>How do you see it?</div>
          <div style={{ color: C.textDim, marginBottom: 14 }}>
            Three choices. They shape which signals matter to you.
          </div>

          <div style={{ fontSize: 11, color: C.textMid, marginBottom: 6 }}>DIRECTION</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {DIRECTIONS.map((d) => (
              <Choice key={d.id} active={direction === d.id} color={d.color}
                      onClick={() => setDirection(d.id)}>{d.label}</Choice>
            ))}
          </div>

          <div style={{ fontSize: 11, color: C.textMid, marginBottom: 6 }}>STYLE</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {STYLES.map((v) => (
              <Choice key={v} active={style === v} onClick={() => setStyle(v)}>
                {v.toUpperCase()}
              </Choice>
            ))}
          </div>

          <div style={{ fontSize: 11, color: C.textMid, marginBottom: 6 }}>TIMEFRAME</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {TIMEFRAMES.map((t) => (
              <Choice key={t.id} active={timeframe === t.id} onClick={() => setTimeframe(t.id)}>
                {t.label}
              </Choice>
            ))}
          </div>
          {timeframe && (
            <div style={{ color: C.textDim, fontSize: 11, marginTop: 6 }}>
              {TIMEFRAMES.find((t) => t.id === timeframe)?.desc}
            </div>
          )}

          {/* ⚠ Stated before the first choice, not buried at the end. */}
          <div style={{ color: C.textDim, fontSize: 11, marginTop: 18, lineHeight: 1.5 }}>
            Quant Signal surfaces signal intelligence. You author the thesis.
            Nothing here is investment advice.
          </div>
        </>
      )}

      {/* ── STEP 2 — name ── */}
      {step === 2 && (
        <>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Name your thesis</div>
          <div style={{ color: C.textDim, marginBottom: 14 }}>
            Something you will recognise in three months.
          </div>
          <input
            style={{ ...s.inp, width: "100%", minHeight: 44 }}
            value={thesisName}
            maxLength={NAME_MAX}
            placeholder="e.g. AI Power Demand"
            onChange={(e) => setThesisName(e.target.value)}
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
            <span style={{ fontSize: 11, color: thesisName && !canStep2 ? C.orange : C.textDim }}>
              {thesisName && !canStep2
                ? "Letters, numbers, spaces and hyphens only."
                : " "}
            </span>
            <span style={{ fontSize: 11, color: C.textDim }}>
              {thesisName.length}/{NAME_MAX}
            </span>
          </div>
        </>
      )}

      {/* ── STEP 3 — sectors ── */}
      {step === 3 && (
        <>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Where are you looking?</div>
          <div style={{ color: C.textDim, marginBottom: 14 }}>
            Up to {MAX_SECTORS} sectors. {selectedSectors.length} of {MAX_SECTORS} chosen.
          </div>
          <div style={{
            display: "grid", gap: 8,
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          }}>
            {Object.keys(taxonomy).map((sec) => {
              const on = selectedSectors.includes(sec);
              const locked = !on && selectedSectors.length >= MAX_SECTORS;
              return (
                <button
                  key={sec}
                  onClick={() => toggleSector(sec)}
                  title={locked
                    ? `Maximum ${MAX_SECTORS} sectors per thesis. Deselect a sector to change.`
                    : sec}
                  style={{
                    textAlign: "left", padding: 10, borderRadius: 3, minHeight: 66,
                    cursor: locked ? "not-allowed" : "pointer", fontFamily: font,
                    background: on ? `${C.green}18` : "transparent",
                    border: `1px solid ${on ? C.green : C.border}`,
                    opacity: locked ? 0.4 : 1,
                    color: C.text,
                  }}
                >
                  <div style={{ fontSize: 15 }}>
                    {SECTOR_ICONS[sec] || "•"} {locked ? "🔒" : ""}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: on ? C.green : C.text }}>
                    {sec}
                  </div>
                  <div style={{ fontSize: 10, color: C.textDim }}>
                    {(taxonomy[sec] || []).length} tickers available
                  </div>
                </button>
              );
            })}
          </div>
          {selectedSectors.length >= MAX_SECTORS && (
            <div style={{ fontSize: 11, color: C.textDim, marginTop: 10 }}>
              Maximum {MAX_SECTORS} sectors per thesis. Deselect a sector to change.
            </div>
          )}
        </>
      )}

      {/* ── STEP 4 — tickers ── */}
      {step === 4 && (
        <>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Which names?</div>
          <div style={{ color: C.textDim, marginBottom: 14 }}>
            Pick from each sector, or search for any ticker.
          </div>
          {selectedSectors.map((sec) => {
            const chosen = selectedTickers[sec] || [];
            const atLimit = chosen.length >= MAX_TICKERS_PER_SECTOR;
            const rows = results[sec] || [];
            return (
              <div key={sec} style={{ ...s.panel, padding: 12, marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <span style={{ fontWeight: 700 }}>{SECTOR_ICONS[sec] || "•"} {sec}</span>
                  <span style={{ fontSize: 11, color: atLimit ? C.orange : C.textDim }}>
                    {chosen.length} of {MAX_TICKERS_PER_SECTOR} max
                  </span>
                </div>

                <input
                  style={{ ...s.inp, width: "100%", minHeight: 40, marginBottom: 8 }}
                  placeholder="Search for any ticker →"
                  value={search[sec] || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSearch((p) => ({ ...p, [sec]: v }));
                    runSearch(sec, v);
                  }}
                />
                {searching[sec] && (
                  <div style={{ fontSize: 11, color: C.textDim, marginBottom: 6 }}>Searching…</div>
                )}
                {!searching[sec] && (search[sec] || "").trim() && rows.length === 0 && (
                  <div style={{ fontSize: 11, color: C.orange, marginBottom: 6 }}>
                    Ticker not found — verify the symbol and try again.
                  </div>
                )}
                {rows.map((r) => (
                  <div key={`f-${r.ticker}`} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "4px 0", fontSize: 12,
                  }}>
                    <span>
                      <strong>{r.ticker}</strong>
                      <span style={{ color: C.textDim }}> {r.name}</span>
                    </span>
                    <button
                      style={{ ...s.btn("green"), padding: "2px 8px", minHeight: 32 }}
                      onClick={() => {
                        toggleTicker(sec, { ...r, custom: true });
                        setSearch((p) => ({ ...p, [sec]: "" }));
                        setResults((p) => ({ ...p, [sec]: [] }));
                      }}
                    >
                      ADD +
                    </button>
                  </div>
                ))}

                {(taxonomy[sec] || []).map((sym) => {
                  const on = chosen.some((t) => t.ticker === sym);
                  const blocked = !on && atLimit;
                  return (
                    <label key={sym} style={{
                      display: "flex", alignItems: "center", gap: 8, padding: "3px 0",
                      opacity: blocked ? 0.45 : 1, cursor: blocked ? "not-allowed" : "pointer",
                    }}>
                      <input
                        type="checkbox" checked={on} disabled={blocked}
                        onChange={() => toggleTicker(sec, { ticker: sym, name: "", sector: sec })}
                      />
                      <span style={{ fontWeight: on ? 700 : 400 }}>{sym}</span>
                    </label>
                  );
                })}

                {chosen.filter((t) => t.custom).length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 11, color: C.textDim }}>
                    Added by search:{" "}
                    {chosen.filter((t) => t.custom).map((t) => (
                      <span key={t.ticker} style={{ ...s.tag, marginRight: 4 }}>
                        {t.ticker} custom
                      </span>
                    ))}
                  </div>
                )}

                {/* ⚠ Says the limit is REACHED, not that more would be unwise.
                    The old copy ("focused theses perform better") read as advice
                    while the checkbox below it still accepted a sixth name; the
                    service rejects that now, so the hint has to state the rule.
                    ⚠ Phrased as a fact about this sector, not as a refusal — the
                    user has two more sectors and 10 more names available. */}
                {atLimit && (
                  <div style={{ fontSize: 11, color: C.orange, marginTop: 6 }}>
                    {MAX_TICKERS_PER_SECTOR} of {MAX_TICKERS_PER_SECTOR} — this sector is full.
                    Remove one to swap, or add names under another sector.
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* ── STEP 5 — signal priorities ── */}
      {step === 5 && (
        <>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>What matters most?</div>
          <div style={{ color: C.textDim, marginBottom: 14 }}>
            Priority changes how much each signal moves your thesis score.
          </div>
          {SIGNAL_TYPES.map(([id, label, blurb]) => (
            <div key={id} style={{ ...s.panel, padding: 10, marginBottom: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between",
                            alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span>
                  {label}{" "}
                  <button
                    onClick={() => setOpenInfo(openInfo === id ? null : id)}
                    aria-label={`About ${label}`}
                    style={{ background: "none", border: `1px solid ${C.border}`, color: C.textDim,
                             borderRadius: "50%", width: 18, height: 18, cursor: "pointer",
                             fontSize: 10, fontFamily: font, lineHeight: 1 }}
                  >?</button>
                </span>
                <span style={{ display: "flex", gap: 4 }}>
                  {["low", "medium", "high"].map((lv) => (
                    <Choice key={lv} active={signalWeights[id] === lv}
                            onClick={() => setSignalWeights((p) => ({ ...p, [id]: lv }))}>
                      {lv.toUpperCase()}
                    </Choice>
                  ))}
                </span>
              </div>
              {openInfo === id && (
                <div style={{ fontSize: 11, color: C.textDim, marginTop: 6 }}>{blurb}</div>
              )}
            </div>
          ))}
        </>
      )}

      {/* ── STEP 6 — the thesis note ── */}
      {step === 6 && (
        <>
          <div style={{ fontSize: 16, fontWeight: 700, color: C.green }}>
            What is the thinking behind your thesis?
          </div>
          <div style={{ color: C.textDim, marginTop: 4, marginBottom: 10 }}>
            Required. Sealed the first time a signal fires, and kept as the
            permanent record of your thinking at authorship.
          </div>

          {/* ⚠⚠ THE FOUR QUESTIONS LIVE ABOVE THE BOX, NOT INSIDE IT. They were
              `placeholder` text, which vanishes on the first keystroke — the prompt
              disappeared at exactly the moment the user began answering it, and
              nothing but memory carried them through the other three. Rendered
              here they stay legible for the whole of the writing. */}
          <div style={{ ...s.panel, padding: 12, marginBottom: 8 }}>
            <div style={{ fontSize: 11, color: C.textMid, letterSpacing: "0.12em",
                          marginBottom: 8 }}>
              ANSWER IN YOUR OWN WORDS
            </div>
            {NOTE_QUESTIONS.map((q) => (
              <div key={q} style={{ display: "flex", gap: 8, marginTop: 5,
                                    fontSize: 12, color: C.textDim, lineHeight: 1.6 }}>
                <span style={{ color: C.green }}>·</span>
                <span>{q}</span>
              </div>
            ))}
          </div>

          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
            style={{
              ...s.panel, width: "100%", minHeight: 200, padding: 12,
              fontFamily: font, fontSize: 13, color: C.text, lineHeight: 1.7,
              resize: "vertical", boxSizing: "border-box",
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "center", marginTop: 4 }}>
            {/* ⚠ Says how many MORE are needed rather than only the count. A bare
                "63 / 2000" beside a disabled button explains nothing about why. */}
            <span style={{ fontSize: 11, color: canStep6 ? C.textDim : C.orange }}>
              {canStep6
                ? "Ready."
                : `${NOTE_MIN - note.trim().length} more characters needed.`}
            </span>
            <span style={{
              fontSize: 11,
              color: note.length >= NOTE_MAX ? C.red
                : note.length >= NOTE_WARN ? C.orange : C.textDim,
            }}>
              {note.length} / {NOTE_MAX}
            </span>
          </div>
        </>
      )}

      {/* ── nav ── */}
      <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
        {step > 1 && (
          <button style={{ ...s.btn(), minHeight: 44 }} onClick={() => setStep(step - 1)}>
            ← BACK
          </button>
        )}
        {step < STEPS && (
          <button
            style={{ ...s.btn("green"), minHeight: 44 }}
            disabled={
              (step === 1 && !canStep1) || (step === 2 && !canStep2) ||
              (step === 3 && !canStep3) || (step === 4 && !canStep4)
            }
            onClick={() => setStep(step + 1)}
          >
            NEXT →
          </button>
        )}
        {/* ⚠⚠ THE SKIP IS GONE, AND SO IS THE UNGATED STEP. This read "SKIP — GO
            TO REVIEW" beside "SAVE NOTE", on the reasoning that the note was
            optional and nagging for it turned an invitation into a toll. The note
            is REQUIRED now — thesis_service rejects a create without one — so a
            skip button offers a route that ends in a 400 at the final step, after
            six screens of work.
            ⚠ Gated on canStep6 rather than hidden: a disabled button beside the
            "N more characters needed" counter says what is missing. A vanished
            button says nothing at all. */}
        {step === STEPS && (
          <button
            style={{ ...s.btn("green"), minHeight: 44 }}
            disabled={!canStep6}
            onClick={() => setReview(true)}
          >
            REVIEW →
          </button>
        )}
      </div>
    </div>
  );
}

export default ThesisBuilder;
